import { forwardRef, ReactNode } from 'react';
import { Box, InputBase, InputBaseProps, styled } from '@mui/material';

// Styled components
const InputContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  padding: '10px 14px',
  alignItems: 'center',
  gap: '8px',
  alignSelf: 'stretch',
  borderRadius: '8px',
  border: '1px solid var(--Gray-300, #D0D5DD)',
  background: 'var(--White, #FFF)',
  transition: 'border-color 0.3s ease',
  boxSizing: 'border-box',
  '&:hover': {
    borderColor: theme.palette.mode === 'dark' ? '#565656' : '#B3B3B3',
  },
  '&:focus-within': {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
  },
}));

const StyledInputBase = styled(InputBase)({
  flex: 1,
  width: '100%',
  margin: 0,
  padding: 0,
  '&.MuiInputBase-root': {
    margin: 0,
    padding: 0,
    color: '#344054',
    fontFamily: 'Inter, sans-serif',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '24px',
  },
  '& .MuiInputBase-input': {
    margin: 0,
    padding: 0,
    color: '#344054',
    fontFamily: 'Inter, sans-serif',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '24px',
    '&::placeholder': {
      color: '#667085',
      opacity: 1,
    },
  },
});

export interface TextInputProps extends Omit<InputBaseProps, 'startAdornment' | 'endAdornment'> {
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  containerProps?: React.ComponentProps<typeof Box>;
  error?: boolean;
  width?: string | number;
  minWidth?: string | number;
  maxWidth?: string | number;
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  ({ startIcon, endIcon, containerProps, error, ...props }, ref) => {
    // Extract width-related props for the container
    const { width, minWidth, maxWidth, sx, ...otherProps } = props;

    // Prepare container styles
    const containerSx = {
      ...(error && {
        borderColor: 'error.main',
        '&:focus-within': {
          borderColor: 'error.main',
          boxShadow: (theme: any) => `0 0 0 2px ${theme.palette.error.main}20`,
        },
      }),
      // Apply width-related styles directly to container
      ...(width !== undefined && { width }),
      ...(minWidth !== undefined && { minWidth }),
      ...(maxWidth !== undefined && { maxWidth }),
      ...containerProps?.sx,
    };

    return (
      <InputContainer {...containerProps} sx={containerSx}>
        {startIcon && (
          <Box
            sx={{
              display: 'flex',
              color: '#667085',
              marginRight: 0,
              padding: 0,
            }}
          >
            {startIcon}
          </Box>
        )}
        <StyledInputBase
          ref={ref}
          {...otherProps}
          sx={{
            color: '#344054',
            fontFamily: 'Inter, sans-serif !important',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: '24px',
            margin: 0,
            padding: 0,
            '& .MuiInputBase-input': {
              margin: 0,
              padding: 0,
            },
            ...sx, // Apply remaining sx props to input
          }}
          inputProps={{
            style: {
              margin: 0,
              padding: 0,
            },
            ...props.inputProps,
          }}
        />
        {endIcon && (
          <Box
            sx={{
              display: 'flex',
              color: '#667085',
              marginLeft: 0,
              padding: 0,
            }}
          >
            {endIcon}
          </Box>
        )}
      </InputContainer>
    );
  },
);

TextInput.displayName = 'TextInput';

export default TextInput;
